/**
 * Default in-app path after login when no deep-link `from` state is present.
 * Parents with no active-semester enrollments go to Enroll first; otherwise Dashboard.
 */

import type { User } from '@lambda/shared/types';
import { enrollmentsApi } from '@services/enrollmentsApi';
import { getActiveSemester } from '@services/publicLandingApi';

/** Must match `STORAGE_KEY` in AuthContext. */
const AUTH_STORAGE_KEY = 'bhg-auth';

/** Read session written by AuthContext (e.g. immediately after 2FA completes). */
export function readStoredSession(): { user: User; token: string } | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { user?: User; token?: string };
    if (parsed?.user && parsed?.token) return { user: parsed.user, token: parsed.token };
  } catch {
    /* ignore */
  }
  return null;
}

function isParentUser(user: User): boolean {
  return user.role === 'parent';
}

/**
 * Returns `/enroll` or `/dashboard` for parents based on active-semester enrollments;
 * non-parents always `/dashboard`.
 */
export async function getDefaultAppPath(token: string, user: User): Promise<string> {
  if (!isParentUser(user)) {
    return '/dashboard';
  }

  const active = await getActiveSemester();
  if (!active?.semesterId) {
    return '/enroll';
  }

  try {
    const res = await enrollmentsApi.list(token, { parentUserId: user.userId });
    const enrollments = res.data?.enrollments ?? [];
    const forActive = enrollments.filter(
      (e) => e.semesterId === active.semesterId && !e.isDeleted
    );
    return forActive.length > 0 ? '/dashboard' : '/enroll';
  } catch {
    return '/enroll';
  }
}
